/**
 * Reading an `ExternalPort` as a relationship, rather than as geometry.
 *
 * A port is the pill on a subspace wall standing in for a peer that lives
 * outside it. Everything about WHERE it sits is in `portPlacement.ts`; this is
 * everything about what it MEANS — which is what the inspectors need, and what
 * three of them were each answering slightly differently.
 *
 * Two things live here because both were duplicated and both were wrong in one
 * of their copies:
 *
 *   - `portAsEdge` — a port addressed as a connection. `buildEdgeSelectLookup`
 *     had this and `findBoardEdge` did not, so which details surface you were
 *     looking at decided whether clicking a pill showed anything at all.
 *   - `portHalves` — splitting a merged mutual port back into its two
 *     directions, for panels that list "calls out" and "called by" separately.
 */
import type { BoardEdge, CallDetail, ExternalPort } from "@archlang/scene/board";
import type { BoundaryMark } from "@archlang/scene/arch-model";

/**
 * The view-BOUNDARY nubs for one node (VIEWS §7), as the ports the board draws.
 *
 * A boundary stub marks a call leaving the VIEW rather than the space: the peer
 * exists in the model but was not projected, so there is no node to draw an
 * arrow to. Plan marks arrive keyed on origin qualified names and are bridged
 * to a board id by the caller (a copied origin shows its own stubs on each
 * copy).
 *
 * They arrive DIRECTION-KEYED, because the plan's `hide … outside` /
 * `style … outside` rules select on direction and so must be applied before
 * anything merges. A pill, though, is per PEER — the same rule as a real
 * cross-space port — so an absent peer talked to both ways becomes one nub
 * reading ⇄ rather than two on opposite walls.
 */
export function boundaryPortsForMark(
  mark: BoundaryMark,
  boardId: string,
  paletteColor: (name: string) => string,
): ExternalPort[] {
  const byPeer = new Map<string, BoundaryMark["stubs"][number][]>();
  for (const s of mark.stubs) {
    const prev = byPeer.get(s.peerKey);
    if (prev) prev.push(s);
    else byPeer.set(s.peerKey, [s]);
  }
  return [...byPeer].map(([peerKey, group]) => {
    const first = group[0]!;
    const direction = group.some((g) => g.direction !== first.direction)
      ? ("inout" as const)
      : first.direction;
    let step = 0;
    return {
      id: `${boardId}|boundary|${peerKey}`,
      direction,
      peerLabel: peerKey.split(".").at(-1) ?? peerKey,
      peerId: peerKey,
      // `details.length` is the ×N badge AND a space-cache term, so every
      // dropped call from BOTH halves has to land here.
      details: group.flatMap((g) =>
        Array.from({ length: g.count }, () => ({
          fromId: g.direction === "out" ? boardId : peerKey,
          toId: g.direction === "out" ? peerKey : boardId,
          fromPath: g.direction === "out" ? mark.nodeKey : peerKey,
          toPath: g.direction === "out" ? peerKey : mark.nodeKey,
          stepIndex: step++,
        })),
      ),
      boundary: true,
      // Dashed pill border — the call continues past the view's edge; a
      // `style … outside` overlay rides the same channel, and its colour may be
      // a palette NAME, so it resolves here (§8.3.1).
      widgetStyle: {
        dashed: true,
        ...(first.style ?? {}),
        ...(first.style?.color ? { color: paletteColor(first.style.color) } : {}),
      },
    };
  });
}

/**
 * A port as the connection it stands for.
 *
 * Owner → peer is canonical, including for `inout`, which has no single
 * direction of its own. `buildEntityDetails` orients the same address the same
 * way, so the native inspector and the web one agree about which way an arrow
 * points.
 */
export function portAsEdge(port: ExternalPort, ownerId: string): BoardEdge {
  return {
    id: port.id,
    from: port.direction === "in" ? port.peerId : ownerId,
    to: port.direction === "in" ? ownerId : port.peerId,
    details: port.details,
  } as BoardEdge;
}

/** Is this deep endpoint inside `ancestorId`'s subtree? */
export type UnderFn = (deepId: string, ancestorId: string) => boolean;

/** Walk `moduleParentById` to answer {@link UnderFn}. Cycle-guarded: a parent
 *  map is built from a tree, but this runs on every selection. */
export function ancestryTest(parentById: ReadonlyMap<string, string> | undefined): UnderFn {
  return (deepId, ancestorId) => {
    const seen = new Set<string>();
    let cur: string | undefined = deepId;
    while (cur && !seen.has(cur)) {
      if (cur === ancestorId) return true;
      seen.add(cur);
      cur = parentById?.get(cur);
    }
    return false;
  };
}

/**
 * The port's calls grouped by the direction each one runs, ready to become one
 * connection row apiece.
 *
 * The BOARD merges a peer called both ways into a single `inout` pill: identity
 * is (owner, peer), and one relationship should be one thing on screen. The
 * connection PANELS ask a different question — "what does this call" and "what
 * calls this" are lists a reader scans separately — so there the same port has
 * to appear once under each heading, and a merged port that only showed up
 * under one would silently drop half a module's connections from the other.
 *
 * A one-way port yields its single half unchanged, so the common case allocates
 * nothing new. A call is INBOUND when its deep caller sits inside the peer's
 * subtree; the details record the concrete modules at each end, not the
 * aggregated space-level ancestors, so the peer id is the only thing that can
 * decide it.
 */
export function portHalves(
  port: ExternalPort,
  under: UnderFn,
): readonly (readonly ["out" | "in", readonly CallDetail[]])[] {
  if (port.direction !== "inout") return [[port.direction, port.details]];
  const inbound: CallDetail[] = [];
  const outbound: CallDetail[] = [];
  for (const d of port.details) {
    (under(d.fromId, port.peerId) ? inbound : outbound).push(d);
  }
  const halves: (readonly ["out" | "in", readonly CallDetail[]])[] = [];
  if (outbound.length > 0) halves.push(["out", outbound]);
  if (inbound.length > 0) halves.push(["in", inbound]);
  return halves;
}
